import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, Trash2, ImagePlus, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  adminListTopics, adminUploadDiagram, adminDeleteDiagram,
  adminListTopicDiagrams, diagramImageUrlAdmin,
} from "@/lib/api";

const KEY_STORAGE = "reviseai_admin_key";

export default function AdminDiagrams() {
  const navigate = useNavigate();
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORAGE) || "");
  const [keyDraft, setKeyDraft] = useState("");
  const [topics, setTopics] = useState([]);
  const [filter, setFilter] = useState({ board: "", subject: "" });
  const [selectedId, setSelectedId] = useState("");
  const [diagrams, setDiagrams] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [form, setForm] = useState({ file: null, title: "", caption: "" });

  // Fetch topics once we have a key.
  useEffect(() => {
    if (!key) return;
    setLoadingTopics(true);
    adminListTopics(key)
      .then((rows) => setTopics(rows))
      .catch((err) => {
        const detail = err?.response?.data?.detail || "Admin fetch failed";
        toast.error(detail);
        if (err?.response?.status === 401) {
          sessionStorage.removeItem(KEY_STORAGE);
          setKey("");
        }
      })
      .finally(() => setLoadingTopics(false));
  }, [key]);

  // Load existing diagrams for the chosen topic (admin-gated endpoint).
  useEffect(() => {
    if (!selectedId || !key) {
      setDiagrams([]);
      return;
    }
    adminListTopicDiagrams(selectedId, key)
      .then(setDiagrams)
      .catch(() => setDiagrams([]));
  }, [selectedId, key]);

  const boards = useMemo(() => [...new Set(topics.map((t) => t.exam_board))], [topics]);
  const subjects = useMemo(
    () => [...new Set(topics.filter((t) => !filter.board || t.exam_board === filter.board).map((t) => t.subject))],
    [topics, filter.board]
  );
  const filteredTopics = useMemo(
    () => topics.filter((t) =>
      (!filter.board || t.exam_board === filter.board) &&
      (!filter.subject || t.subject === filter.subject)
    ),
    [topics, filter]
  );

  const saveKey = (e) => {
    e.preventDefault();
    const k = keyDraft.trim();
    if (!k) return toast.error("Enter the admin key");
    sessionStorage.setItem(KEY_STORAGE, k);
    setKey(k);
    setKeyDraft("");
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!selectedId) return toast.error("Pick a topic first");
    if (!form.file) return toast.error("Pick an image");
    if (!form.title.trim()) return toast.error("Add a title");
    setUploading(true);
    try {
      const newDoc = await adminUploadDiagram(selectedId, key, form.file, form.title.trim(), form.caption.trim());
      toast.success("Diagram uploaded");
      setForm({ file: null, title: "", caption: "" });
      e.target.reset();
      // Optimistic append, avoids a round-trip and works even if the list refresh fails.
      setDiagrams((d) => [...d, newDoc]);
      // Still refresh in the background to pick up any admin edits elsewhere.
      adminListTopicDiagrams(selectedId, key).then(setDiagrams).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this diagram? This can't be undone.")) return;
    try {
      await adminDeleteDiagram(id, key);
      setDiagrams((d) => d.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  // ---------- Key gate ----------
  if (!key) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950 flex items-center justify-center p-6">
        <form onSubmit={saveKey} className="nb-card p-8 w-full max-w-md space-y-4" data-testid="admin-key-form">
          <div className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" strokeWidth={2.5} />
            <h1 className="font-display text-2xl font-black">Admin access</h1>
          </div>
          <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
            Paste the admin key (RECONCILE_KEY). Stays only in this browser tab.
          </p>
          <input
            type="password"
            value={keyDraft}
            onChange={(e) => setKeyDraft(e.target.value)}
            placeholder="rcl_..."
            autoFocus
            data-testid="admin-key-input"
            className="nb-input"
          />
          <button type="submit" className="nb-button px-4 py-3 w-full bg-[#D4C4FB] dark:bg-[#B794F4] text-black" data-testid="admin-key-submit">
            Unlock
          </button>
        </form>
      </div>
    );
  }

  // ---------- Main admin UI ----------
  const selectedTopic = topics.find((t) => t.id === selectedId);

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950" data-testid="admin-diagrams-page">
      <header className="nb-border border-l-0 border-r-0 border-t-0 bg-white dark:bg-zinc-900 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/")}
            data-testid="admin-exit"
            className="nb-border rounded-xl px-3 py-2 font-bold text-sm flex items-center gap-2 hover:nb-shadow"
          >
            <ArrowLeft className="w-4 h-4" /> Exit
          </button>
          <div className="font-display font-extrabold text-xl flex items-center gap-2">
            <ImagePlus className="w-5 h-5" /> Verified Diagrams
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/admin/console")}
              className="nb-border rounded-xl px-3 py-2 text-xs font-bold hover:nb-shadow"
              data-testid="admin-nav-console"
              title="Admin console"
            >
              Console
            </button>
            <button
              onClick={() => {
                sessionStorage.removeItem(KEY_STORAGE);
                setKey("");
              }}
              className="nb-border rounded-xl px-3 py-2 text-xs font-bold hover:nb-shadow"
              data-testid="admin-lock"
            >
              Lock
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-6 sm:p-10">
        {/* Topic picker */}
        <div className="nb-card p-5 mb-6">
          <div className="label-caps mb-3">Pick a topic</div>
          {loadingTopics ? (
            <div className="font-bold text-zinc-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading topics…
            </div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-3">
              <select
                value={filter.board}
                onChange={(e) => setFilter({ board: e.target.value, subject: "" })}
                className="nb-input"
                data-testid="admin-board-select"
              >
                <option value="">All boards</option>
                {boards.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <select
                value={filter.subject}
                onChange={(e) => setFilter((f) => ({ ...f, subject: e.target.value }))}
                className="nb-input"
                data-testid="admin-subject-select"
              >
                <option value="">All subjects</option>
                {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="nb-input"
                data-testid="admin-topic-select"
              >
                <option value="">Choose topic ({filteredTopics.length})</option>
                {filteredTopics.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.exam_board.replace(" GCSE", "")} · {t.subject} · {t.topic_name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {selectedTopic && (
          <>
            {/* Upload form */}
            <form onSubmit={upload} className="nb-card p-5 mb-6" data-testid="admin-upload-form">
              <div className="label-caps mb-3">Add a diagram to <strong className="text-black dark:text-white">{selectedTopic.topic_name}</strong></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                  onChange={(e) => setForm((f) => ({ ...f, file: e.target.files?.[0] || null }))}
                  className="nb-input"
                  required
                  data-testid="admin-file-input"
                />
                <input
                  type="text"
                  placeholder="Title (e.g. Electrolysis apparatus)"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  maxLength={120}
                  className="nb-input"
                  required
                  data-testid="admin-title-input"
                />
              </div>
              <textarea
                placeholder="Caption / exam-board context (optional)"
                value={form.caption}
                onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))}
                maxLength={500}
                rows={2}
                className="nb-input mt-3"
                data-testid="admin-caption-input"
              />
              <button
                type="submit"
                disabled={uploading}
                className="nb-button px-4 py-3 mt-3 flex items-center gap-2 bg-[#9DFFB0] dark:bg-[#68D391] text-black disabled:opacity-50"
                data-testid="admin-upload-submit"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Uploading…" : "Upload diagram"}
              </button>
              <p className="text-xs text-zinc-500 mt-2">PNG, JPEG, WebP, GIF or SVG · max 5 MB</p>
            </form>

            {/* Existing diagrams */}
            <div className="nb-card p-5" data-testid="admin-diagram-list">
              <div className="label-caps mb-4">
                Current diagrams ({diagrams.length})
              </div>
              {diagrams.length === 0 ? (
                <div className="text-sm font-medium text-zinc-500">No diagrams uploaded yet for this topic.</div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {diagrams.map((d) => (
                    <div key={d.id} className="nb-border rounded-xl p-3 bg-white dark:bg-zinc-900" data-testid={`admin-diagram-${d.id}`}>
                      <div className="aspect-video nb-border rounded-lg overflow-hidden bg-zinc-100 dark:bg-zinc-800 mb-2 flex items-center justify-center">
                        <img
                          src={diagramImageUrlAdmin(d.id, key)}
                          alt={d.title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="font-bold text-sm truncate">{d.title}</div>
                      {d.caption && <div className="text-xs text-zinc-500 line-clamp-2 mt-1">{d.caption}</div>}
                      <button
                        onClick={() => remove(d.id)}
                        className="mt-2 nb-border rounded-lg px-2 py-1 text-xs font-bold flex items-center gap-1 hover:nb-shadow"
                        data-testid={`admin-delete-${d.id}`}
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
